import { http } from '@/app/lib/http/client'

export type SettingType =
    | 'string'
    | 'boolean'
    | 'integer'
    | 'decimal'
    | 'json'
    | 'color'
    | 'image'
    | 'timezone'

export interface ImageSettingValue {
    disk: string
    path: string
    url: string
    filename?: string
    mime_type?: string
    size?: number
}

export interface SettingItem<T = unknown> {
    key: string
    type: SettingType
    value: T | null
    is_public: boolean
    updated_at: string | null
    is_configured?: boolean
}

export type SettingsPayload = Record<string, unknown>

export interface ReportMailSenderSetting {
    id: number
    name: string
    code: string
    is_active: boolean
    report_mail_from_address: string | null
    report_mail_from_name: string | null
    report_mail_username: string | null
    report_mail_password_configured: boolean
    configured: boolean
    report_mail_password?: string
    clear_report_mail_password?: boolean
}

export interface UpdateReportMailSenderSetting {
    sucursal_id: number
    report_mail_from_address: string | null
    report_mail_from_name: string | null
    report_mail_username: string | null
    report_mail_password?: string
    clear_report_mail_password?: boolean
}

export interface PlatformSettingsPayload {
    settings?: SettingsPayload
    report_mail_senders?: UpdateReportMailSenderSetting[]
}

export interface PlatformSettingsResult {
    settings: SettingItem[]
    report_mail_senders: ReportMailSenderSetting[]
}

export const settingsService = {
    async getBrandingSettings(): Promise<SettingItem[]> {
        const response = await http.get('/settings/branding')
        return response.data.data
    },

    async getSettings(): Promise<SettingItem[]> {
        const response = await http.get('/settings')
        return response.data.data
    },

    async getPublicSettings(sucursalId: number): Promise<SettingItem[]> {
        const response = await http.get('/settings/public', {
            params: { sucursal_id: sucursalId },
        })
        return response.data.data
    },

    async updateSettings(settings: SettingsPayload): Promise<SettingItem[]> {
        const response = await http.put('/settings', { settings })
        return response.data.data
    },

    async getReportMailSenders(): Promise<ReportMailSenderSetting[]> {
        const response = await http.get('/settings/report-mail-senders')
        return response.data.data
    },

    async updatePlatformSettings(payload: PlatformSettingsPayload): Promise<PlatformSettingsResult> {
        const response = await http.put('/settings/platform', payload)
        return response.data.data
    },

    async uploadAsset(key: string, file: File): Promise<SettingItem<ImageSettingValue>> {
        const formData = new FormData()
        formData.append('file', file)

        const response = await http.post(`/settings/assets/${encodeURIComponent(key)}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })

        return response.data.data
    },

    async updatePosPin(data: { password: string; pin: string; pin_confirmation: string }): Promise<void> {
        await http.put('/settings/pos-register-pin', data)
    },
}

export function settingsRecord(settings: SettingItem[]): Record<string, SettingItem> {
    return settings.reduce<Record<string, SettingItem>>((acc, setting) => {
        acc[setting.key] = setting
        return acc
    }, {})
}

export function settingValue<T>(settings: SettingItem[], key: string, fallback: T): T {
    const value = settings.find((setting) => setting.key === key)?.value
    return value === null || value === undefined ? fallback : (value as T)
}
